// Financeiro — receita mensal, recebíveis (donut), top categorias OMIE, ticket médio.
import { useEffect, useState } from "react";
import { api } from "../../api.ts";
import { LineChart, DonutChart, HorizontalBar } from "../../components/charts/index.ts";
import { fmtBRL, fmtBRLFull, fmtNum } from "../../lib/format.ts";

interface Payload {
  summary: {
    received_this_month: number;
    received_prev_month: number;
    avg_ticket_this_month: number;
  };
  monthly_series: Array<{ month: string; revenue: number; transactions: number }>;
  receivables_mix: {
    paid_90d: number;
    open_amount: number;
    overdue_amount: number;
    open_count: number;
    overdue_count: number;
  };
  top_categories_30d: Array<{ name: string | null; code: string | null; revenue: number; transactions: number }>;
}

const MONTHS_PT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

function monthLabel(iso: string) {
  const d = new Date(iso);
  return `${MONTHS_PT[d.getUTCMonth()]}/${String(d.getUTCFullYear()).slice(-2)}`;
}

export function FinanceiroPanel({ slug }: { slug: string }) {
  const [data, setData] = useState<Payload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<Payload>(`/api/t/${slug}/tactical/financeiro`).then(setData).catch((e) => setErr(e.message));
  }, [slug]);

  if (err) return <div className="card"><p style={{ color: "var(--red)" }}>erro: {err}</p></div>;
  if (!data) return <div className="center-loading">carregando…</div>;

  const monthlyData = data.monthly_series.map((m) => ({
    label: monthLabel(m.month),
    value: m.revenue,
  }));

  const arSlices = [
    { name: "Pagos (90d)", value: data.receivables_mix.paid_90d, color: "#047857" },
    { name: "Em aberto", value: data.receivables_mix.open_amount, color: "#b45309" },
    { name: "Vencidos", value: data.receivables_mix.overdue_amount, color: "#b91c1c" },
  ].filter((s) => s.value > 0);

  const arTotal = arSlices.reduce((s, c) => s + c.value, 0);

  const topCats = data.top_categories_30d
    .filter((c) => (c.revenue ?? 0) > 0)
    .slice(0, 10)
    .map((c) => ({ name: (c.name ?? c.code ?? "(sem categoria)").slice(0, 40), value: c.revenue }));

  const deltaPct = data.summary.received_prev_month > 0
    ? ((data.summary.received_this_month - data.summary.received_prev_month) / data.summary.received_prev_month) * 100
    : null;

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="label">Recebido · mês corrente</div>
          <div className="value">{fmtBRL(data.summary.received_this_month)}</div>
          <div className="meta">
            <span className="mono">vs {fmtBRL(data.summary.received_prev_month)} mês ant.</span>
            {deltaPct !== null && (
              <span className={`pill ${deltaPct >= 0 ? "" : "red"}`} style={{ marginLeft: ".4rem" }}>
                {deltaPct >= 0 ? "+" : ""}{deltaPct.toFixed(1)}%
              </span>
            )}
          </div>
        </div>
        <div className="kpi-card">
          <div className="label">Ticket médio</div>
          <div className="value">{fmtBRLFull(data.summary.avg_ticket_this_month)}</div>
          <div className="meta"><span className="mono">média dos títulos do mês</span></div>
        </div>
        <div className="kpi-card" style={{
          borderColor: data.receivables_mix.overdue_count > 0 ? "var(--red)" : "var(--border)",
        }}>
          <div className="label" style={{ color: data.receivables_mix.overdue_count > 0 ? "var(--red)" : undefined }}>
            Vencidos ⚠
          </div>
          <div className="value">{fmtBRL(data.receivables_mix.overdue_amount)}</div>
          <div className="meta"><span className="mono">{data.receivables_mix.overdue_count} títulos</span></div>
        </div>
      </div>

      <div className="section">
        <div className="section-head">
          <div>
            <p className="eyebrow">Últimos 12 meses</p>
            <h2>Receita Mensal · ERP</h2>
          </div>
        </div>
        <div className="card">
          <LineChart data={monthlyData} height={280} formatValue={fmtBRL} />
        </div>
      </div>

      {arSlices.length > 0 && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Recebíveis ERP</p>
              <h2>Composição (pagos 90d + abertos + vencidos)</h2>
            </div>
          </div>
          <div className="grid g2">
            <div className="card">
              <DonutChart
                data={arSlices}
                centerLabel={{ value: fmtBRL(arTotal), sub: "total" }}
                height={300}
              />
            </div>
            <div className="card">
              <table className="simple">
                <thead><tr><th>Estado</th><th style={{ textAlign: "right" }}>Valor</th><th style={{ textAlign: "right" }}>%</th></tr></thead>
                <tbody>
                  {arSlices.map((s, i) => (
                    <tr key={i}>
                      <td>
                        <span style={{
                          display: "inline-block", width: 10, height: 10, borderRadius: 2,
                          background: s.color, marginRight: ".5rem", verticalAlign: "middle",
                        }} />
                        <strong>{s.name}</strong>
                      </td>
                      <td style={{ textAlign: "right" }} className="mono">{fmtBRL(s.value)}</td>
                      <td style={{ textAlign: "right" }} className="mono">
                        {arTotal > 0 ? ((s.value / arTotal) * 100).toFixed(1) : "0"}%
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={3} style={{ paddingTop: ".75rem", color: "var(--text-muted)", fontSize: ".82rem" }}>
                      {fmtNum(data.receivables_mix.open_count)} títulos abertos · {fmtNum(data.receivables_mix.overdue_count)} vencidos
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {topCats.length > 0 && (
        <div className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Últimos 30 dias</p>
              <h2>Top categorias ERP por receita</h2>
            </div>
          </div>
          <div className="card">
            <HorizontalBar data={topCats} height={Math.max(220, topCats.length * 32)} formatValue={fmtBRL} />
          </div>
        </div>
      )}
    </>
  );
}
